/**
 * Life4Earth V2 — Community Needs API Adapter
 *
 * Endpoint: GET /api/PreflightItems/community-needs?userId={guid}
 *
 * Fetches seeking/partial pre-flight items from projects in communities
 * the user belongs to. Used by the Resource Matching Workspace (right panel).
 *
 * Also includes source recommendation submission and retrieval.
 */

import { apiFetch } from './client';
import { normalizeGuid } from '../utils/guid';
import { normalizeCategory } from '../Data/PreFlight/preflightTypes';

// ── Types — API response shapes ─────────────────────────────

export interface CommunityNeed {
  id: string;
  projectId: string;
  projectName: string;
  projectStatus: string | null;
  projectStartDate: string | null;
  projectFinishDate: string | null;
  projectOwnerGuid: string | null;
  projectOwnerName: string | null;
  projectOwnerAvatarUrl: string | null;
  communityEntityGuid: string;
  communityName: string;
  communityEntityType: string;
  phase: string;
  category: string;
  label: string;
  description: string | null;
  requirementType: string;
  targetQuantity: number | null;
  unit: string | null;
  currentQuantity: number;
  state: string;
  priority: string;
  descriptorTags: string[];
  matchingConditions: string | null;
  sourceCount: number;
  createdAt: string;
}

/** Normalized frontend shape */
export interface CommunityNeedItem {
  item_guid: string;
  project_guid: string;
  project_name: string;
  project_status: string | null;
  project_start_date: string | null;
  project_finish_date: string | null;
  project_owner_guid: string | null;
  project_owner_name: string | null;
  project_owner_avatar_url: string | null;
  community_entity_guid: string;
  community_name: string;
  community_entity_type: string;
  phase: string;
  category: string;
  label: string;
  description: string | null;
  requirement_type: string;
  target_quantity: number | null;
  unit: string | null;
  current_quantity: number;
  state: string;
  priority: string;
  descriptor_tags: string[];
  matching_conditions: string | null;
  source_count: number;
  created_at: string;
}

// ── Source types ─────────────────────────────────────────────

export interface SourceRecommendation {
  source_guid: string;
  item_guid: string;
  submitted_by_guid: string;
  submitted_by_name: string;
  url: string | null;
  note: string | null;
  source_type: string;
  review_status: string | null;
  created_at: string;
}

interface SourceApiResponse {
  sourceGuid: string;
  itemGuid: string;
  submittedByGuid: string;
  submittedByName: string | null;
  url: string | null;
  note: string | null;
  sourceType: string;
  reviewStatus: string | null;
  createdAt: string;
}

// ── Shape Converters ─────────────────────────────────────────

function apiResponseToCommunityNeed(r: CommunityNeed): CommunityNeedItem {
  return {
    item_guid: normalizeGuid(r.id),
    project_guid: normalizeGuid(r.projectId),
    project_name: r.projectName,
    project_status: r.projectStatus,
    project_start_date: r.projectStartDate,
    project_finish_date: r.projectFinishDate,
    project_owner_guid: r.projectOwnerGuid ? normalizeGuid(r.projectOwnerGuid) : null,
    project_owner_name: r.projectOwnerName,
    project_owner_avatar_url: r.projectOwnerAvatarUrl ?? null,
    community_entity_guid: normalizeGuid(r.communityEntityGuid),
    community_name: r.communityName,
    community_entity_type: r.communityEntityType,
    phase: r.phase,
    category: normalizeCategory(r.category),
    label: r.label,
    description: r.description,
    requirement_type: r.requirementType,
    target_quantity: r.targetQuantity,
    unit: r.unit,
    current_quantity: r.currentQuantity,
    state: r.state,
    priority: r.priority,
    descriptor_tags: r.descriptorTags || [],
    matching_conditions: r.matchingConditions,
    source_count: r.sourceCount ?? 0,
    created_at: r.createdAt,
  };
}

function apiResponseToSource(r: SourceApiResponse): SourceRecommendation {
  return {
    source_guid: normalizeGuid(r.sourceGuid),
    item_guid: normalizeGuid(r.itemGuid),
    submitted_by_guid: normalizeGuid(r.submittedByGuid),
    submitted_by_name: r.submittedByName ?? '',
    url: r.url,
    note: r.note,
    source_type: r.sourceType,
    review_status: r.reviewStatus,
    created_at: r.createdAt,
  };
}

// ── API Functions ────────────────────────────────────────────

/**
 * Fetch seeking/partial resource needs from projects in the user's communities.
 *
 * @param maxHops — How many hierarchy hops to walk (0 = direct only, 5 = full).
 *   Use 0 for the initial fast load, then call again with higher values for
 *   broader community tiers.
 */
export async function fetchCommunityNeeds(
  userGuid: string,
  maxHops: number = 5
): Promise<CommunityNeedItem[]> {
  const resp = await apiFetch<CommunityNeed[]>(
    `/api/PreflightItems/community-needs?userId=${encodeURIComponent(userGuid)}&maxHops=${maxHops}`
  );
  return resp.map(apiResponseToCommunityNeed);
}

/**
 * Submit a source recommendation (URL and/or note) for a resource need.
 */
export async function submitSource(
  userGuid: string,
  itemGuid: string,
  url: string | null,
  note: string | null,
  sourceType: string = 'external'
): Promise<SourceRecommendation> {
  const resp = await apiFetch<SourceApiResponse>(
    `/api/PreflightItems/sources?userId=${encodeURIComponent(userGuid)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemGuid, url, note, sourceType }),
    }
  );
  return apiResponseToSource(resp);
}

/**
 * Fetch all source recommendations for a specific resource need.
 */
export async function fetchSources(itemGuid: string): Promise<SourceRecommendation[]> {
  const resp = await apiFetch<SourceApiResponse[]>(
    `/api/PreflightItems/${encodeURIComponent(itemGuid)}/sources`
  );
  return resp.map(apiResponseToSource);
}
